import { useState, useEffect, useCallback } from "react"

const STORAGE_KEY = "preset_tutorial_access"
const COOKIE_NAME = "preset_tutorial_gated"
const COOKIE_EXPIRY_DAYS = 365

/**
 * Get cookie value by name
 */
function getCookie(name) {
  if (typeof document === "undefined") return null
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

/**
 * Set cookie with expiry
 */
function setCookie(name, value, days) {
  if (typeof document === "undefined") return
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
}

/**
 * Check if user has tutorial access (synchronous checks only)
 * Returns: true (has access), false (no access), or null (needs async check)
 */
function checkAccessSync() {
  if (typeof window === "undefined") return false

  // Check localStorage (fastest)
  try {
    if (localStorage.getItem(STORAGE_KEY) === "granted") {
      return true
    }
  } catch {
    // localStorage may be unavailable
  }

  // Check our cookie (cross-subdomain backup)
  if (getCookie(COOKIE_NAME) === "true") {
    return true
  }

  // Check for existing HubSpot user token (auto-unlock for known HubSpot users)
  if (window.__hsUserToken) {
    return true
  }

  // If they have hubspotutk, we need to do an async check
  if (getCookie("hubspotutk")) {
    return null // Indicates we need async check
  }

  return false
}

/**
 * Check HubSpot contact status via Netlify function
 */
async function checkHubSpotCustomer(utk) {
  try {
    const response = await fetch(
      `/.netlify/functions/check-hubspot-contact?utk=${encodeURIComponent(utk)}`
    )
    if (!response.ok) return false

    const data = await response.json()
    return data.isCustomer === true
  } catch {
    return false
  }
}

/**
 * Hook to manage tutorial video gating
 *
 * Checks multiple sources to determine access:
 * 1. localStorage (previous form submission)
 * 2. Cookie (cross-subdomain backup)
 * 3. HubSpot user token
 * 4. HubSpot contact lookup (async - checks if they're a customer)
 *
 * @returns {Object} - { hasAccess: boolean, isLoading: boolean, grantAccess: () => void }
 */
function useTutorialAccess() {
  const [hasAccess, setHasAccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Dev-only helper: window.resetTutorialGate() in the browser console
  useEffect(() => {
    const isDevOrPreview =
      process.env.NODE_ENV === "development" ||
      process.env.GATSBY_IS_PREVIEW === "true"
    if (!isDevOrPreview) return
    window.resetTutorialGate = () => {
      localStorage.removeItem(STORAGE_KEY)
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `hubspotutk=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      console.log("Tutorial gate reset. Refresh the page.")
    }
    return () => delete window.resetTutorialGate
  }, [])

  // Check access on mount
  useEffect(() => {
    async function checkAccess() {
      // First do synchronous checks
      const syncResult = checkAccessSync()

      if (syncResult === true) {
        setHasAccess(true)
        setIsLoading(false)
        return
      }

      if (syncResult === false) {
        setHasAccess(false)
        setIsLoading(false)
        return
      }

      // syncResult is null - need to check HubSpot API
      const utk = getCookie("hubspotutk")
      if (utk) {
        const isCustomer = await checkHubSpotCustomer(utk)
        if (isCustomer) {
          // They're a customer - grant access and remember it
          try {
            localStorage.setItem(STORAGE_KEY, "granted")
          } catch {
            // localStorage may be unavailable
          }
          setCookie(COOKIE_NAME, "true", COOKIE_EXPIRY_DAYS)
          setHasAccess(true)
        }
      }

      setIsLoading(false)
    }

    checkAccess()
  }, [])

  // Grant access (called after form submission)
  const grantAccess = useCallback(() => {
    if (typeof window === "undefined") return

    // Set localStorage
    try {
      localStorage.setItem(STORAGE_KEY, "granted")
    } catch {
      // localStorage may be unavailable
    }

    // Set cookie (1 year expiry)
    setCookie(COOKIE_NAME, "true", COOKIE_EXPIRY_DAYS)

    setHasAccess(true)
  }, [])

  return { hasAccess, isLoading, grantAccess }
}

export default useTutorialAccess
